import { useEffect, useState } from 'react';

import {
  type DerivedEventFilterLegacy,
  type FilterLegacy,
  type InputFieldLegacy,
} from '@verantos-dev/types-cohorts';

export const useCanSaveCohortItem = <T>(
  name: string,
  type: T | undefined,
  filters?: (FilterLegacy | DerivedEventFilterLegacy)[]
) => {
  const [isSaveAllowed, setIsSaveAllowed] = useState<boolean>(false);

  // Only allow users to save a subject variable if they have entered a name and selected a type
  useEffect(() => {
    // prevent the user to save if no name is entered
    if (!name || name.trim() === '') {
      setIsSaveAllowed(false);
      return;
    }

    // prevent the user to save if no type is selected
    if (type === undefined) {
      setIsSaveAllowed(false);
      return;
    }

    if (filters !== undefined) {
      // prevent the user to save if no filters are defined
      if (!filters.length) {
        setIsSaveAllowed(false);
        return;
      }

      // prevent the user to save if added filters or constrains are not defined fully

      // find incomplete filter
      const incompletedFilter = filters.find((filter: FilterLegacy | DerivedEventFilterLegacy) => {
        if ('inputFields' in filter) {
          /**
           * if filter type is Filter, check if this filter is incomplete.
           *
           * filter is incomplete if
           * - input field value is empty string.
           * - input field value is undefined.
           * - input field value is less than 0 if its numeric.
           */
          const undefinedValue = filter.inputFields.find((inputField: InputFieldLegacy) => {
            return (
              inputField.value === '' ||
              inputField.value === undefined ||
              (!isNaN(Number(inputField.value)) && Number(inputField.value) < 0)
            );
          });
          return undefinedValue !== undefined;
        } else {
          const { eventName, eventDateAttribute, index, position } = filter;
          /**
           * if filter type is DerivedEventFilter
           *
           * filter is incomplete if
           * - eventName field is not defined or its value is empty or undefined
           * - eventDateAttribute field is not defined or its value is empty or undefined
           * - index field value is empty or undefined if index field is defined (index field is defined only for instance type event)
           * - position field value is empty or undefined if position field is defined (position field is defined only for instance type event)
           */
          return (
            eventName === undefined ||
            eventName.value === '' ||
            eventName.value === undefined ||
            eventDateAttribute === undefined ||
            eventDateAttribute.value === '' ||
            eventDateAttribute.value === undefined ||
            (index !== undefined && (index.value === '' || index.value === undefined)) ||
            (position !== undefined && (position.value === '' || position.value === undefined))
          );
        }
      });

      if (incompletedFilter !== undefined) {
        setIsSaveAllowed(false);
        return;
      }
    }

    // If none of the above cases fail, then we can allow the user to save
    setIsSaveAllowed(true);
  }, [name, type, filters]);

  return {
    isSaveAllowed,
  };
};
